import type { RumBeforeSend, RumInitConfiguration } from '@datadog/browser-rum';

import { getApiBaseUrl } from 'networking/api-config';

export interface DatadogRumConfig extends RumInitConfiguration {
  applicationId: string;
  clientToken: string;
  site: 'datadoghq.eu';
  defaultPrivacyLevel: 'mask';
  service?: string;
  env?: string;
  version?: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackResources: boolean;
  trackUserInteractions: boolean;
  trackLongTasks: boolean;
  allowedTracingUrls: string[];
}

const DEFAULT_SAMPLE_RATE = 100;
const DEFAULT_REPLAY_SAMPLE_RATE = 50;
const ARTIFACT_REVISION_QUERY_PARAMETER = 'artifactRevision';
const REDACTED_QUERY_VALUE = 'REDACTED';

const getRawQueryParameterName = (parameter: string): string => {
  const separatorIndex = parameter.indexOf('=');

  return parameter.slice(
    0,
    separatorIndex === -1 ? parameter.length : separatorIndex
  );
};

const isArtifactRevisionParameter = (parameter: string): boolean => {
  const rawName = getRawQueryParameterName(parameter);

  try {
    return (
      decodeURIComponent(rawName.replaceAll('+', ' ')) ===
      ARTIFACT_REVISION_QUERY_PARAMETER
    );
  } catch {
    return rawName === ARTIFACT_REVISION_QUERY_PARAMETER;
  }
};

const redactArtifactRevision = (url: string): string => {
  const queryStart = url.indexOf('?');

  if (queryStart === -1) {
    return url;
  }

  const fragmentStart = url.indexOf('#', queryStart);
  const queryEnd = fragmentStart === -1 ? url.length : fragmentStart;
  const query = url.slice(queryStart + 1, queryEnd);
  let changed = false;

  const redactedQuery = query
    .split('&')
    .map(parameter => {
      if (!isArtifactRevisionParameter(parameter)) {
        return parameter;
      }

      changed = true;

      return `${getRawQueryParameterName(parameter)}=${REDACTED_QUERY_VALUE}`;
    })
    .join('&');

  return changed
    ? `${url.slice(0, queryStart + 1)}${redactedQuery}${url.slice(queryEnd)}`
    : url;
};

const redactArtifactRevisionBeforeSend: RumBeforeSend = event => {
  if (event.type === 'resource') {
    event.resource.url = redactArtifactRevision(event.resource.url);
  } else if (event.type === 'error' && event.error.resource) {
    event.error.resource.url = redactArtifactRevision(event.error.resource.url);
  }

  return true;
};

const firstDefined = (
  ...values: Array<string | undefined>
): string | undefined => values.find(Boolean);

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
    return fallback;
  }

  return parsed;
};

const parseAllowedTracingUrls = (
  configuredUrls: string | undefined,
  apiBaseUrl: string | undefined
): string[] => {
  const configured = (configuredUrls || '')
    .split(',')
    .map(url => url.trim())
    .filter(Boolean);

  const urls = configured.length
    ? configured
    : (apiBaseUrl || '')
        .split(',')
        .map(url => url.trim())
        .filter(Boolean);

  return Array.from(new Set(urls));
};

export const getDatadogRumConfig = (): DatadogRumConfig | null => {
  const applicationId = firstDefined(
    process.env.DATADOG_RUM_APPLICATION_ID,
    process.env.DATADOG_APPLICATION_ID
  );

  const clientToken = firstDefined(
    process.env.DATADOG_RUM_CLIENT_TOKEN,
    process.env.DATADOG_CLIENT_TOKEN
  );

  if (!applicationId || !clientToken) {
    return null;
  }

  return {
    applicationId,
    clientToken,
    site: 'datadoghq.eu',
    defaultPrivacyLevel: 'mask',
    service: firstDefined(
      process.env.DATADOG_RUM_SERVICE,
      process.env.DATADOG_SERVICE
    ),
    env: firstDefined(process.env.DATADOG_RUM_ENV, process.env.DATADOG_ENV),
    version: firstDefined(
      process.env.DATADOG_RUM_VERSION,
      process.env.DATADOG_VERSION,
      process.env.npm_package_version
    ),
    sessionSampleRate: parseSampleRate(
      firstDefined(
        process.env.DATADOG_RUM_SESSION_SAMPLE_RATE,
        process.env.DATADOG_SESSION_SAMPLE_RATE
      ),
      DEFAULT_SAMPLE_RATE
    ),
    sessionReplaySampleRate: parseSampleRate(
      firstDefined(
        process.env.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
        process.env.DATADOG_SESSION_REPLAY_SAMPLE_RATE
      ),
      DEFAULT_REPLAY_SAMPLE_RATE
    ),
    beforeSend: redactArtifactRevisionBeforeSend,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: parseAllowedTracingUrls(
      process.env.DATADOG_RUM_ALLOWED_TRACING_URLS,
      getApiBaseUrl()
    ),
  };
};
